import { Controller } from "stimulus"

export default class extends Controller {
  static targets = ["slide", "dot", "title", "icon", "tagline"]
  static values = {
    autoRotate: Boolean,
    interval: Number,
    titles: String,
    icons: String,
    taglines: String
  }

  connect() {
    this.currentIndex = 0
    this.typingTimeout = null
    this.cursor = null

    // Parse JSON strings for titles, icons, and taglines
    this.titles = this.hasTitlesValue ? JSON.parse(this.titlesValue) : []
    this.icons = this.hasIconsValue ? JSON.parse(this.iconsValue) : []
    this.taglines = this.hasTaglinesValue ? JSON.parse(this.taglinesValue) : []

    // Set defaults using instance variables
    this.interval = this.hasIntervalValue ? this.intervalValue : 12000
    this.autoRotate = this.hasAutoRotateValue ? this.autoRotateValue : true

    this.showSlide(0, true)

    if (this.autoRotate) {
      this.startAutoRotate()
    }
  }

  disconnect() {
    this.stopAutoRotate()
  }

  next() {
    const nextIndex = (this.currentIndex + 1) % this.slideTargets.length
    this.showSlide(nextIndex, true)
    this.restartAutoRotate()
  }

  previous() {
    const prevIndex = (this.currentIndex - 1 + this.slideTargets.length) % this.slideTargets.length
    this.showSlide(prevIndex, true)
    this.restartAutoRotate()
  }

  goToSlide(event) {
    const index = parseInt(event.currentTarget.dataset.index, 10)
    if (index !== this.currentIndex) {
      this.showSlide(index, true)
      this.restartAutoRotate()
    }
  }

  showSlide(index, animate = true) {
    this.currentIndex = index

    // Clear any existing typing timeout
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout)
    }

    // Hide all slides
    this.slideTargets.forEach((slide, i) => {
      if (i === index) {
        slide.classList.remove("d-none")
        if (animate) {
          slide.classList.add("fade-in")
        }
      } else {
        slide.classList.add("d-none")
        slide.classList.remove("fade-in")
      }
    })

    // Update dots
    this.dotTargets.forEach((dot, i) => {
      if (i === index) {
        dot.classList.add("active")
      } else {
        dot.classList.remove("active")
      }
    })

    // Update title with fade-up animation if titles are provided
    if (this.hasTitleTarget && this.titles.length > index) {
      if (animate) {
        this.animateTitleFadeUp(this.titles[index])
      } else {
        this.titleTarget.textContent = this.titles[index]
      }
    }

    // Update icon with fade-up animation if icons are provided
    if (this.hasIconTarget && this.icons.length > index) {
      if (animate) {
        this.animateIconFadeUp(this.icons[index])
      } else {
        this.iconTarget.className = this.icons[index]
      }
    }

    // Update tagline with typing effect
    if (this.hasTaglineTarget && this.taglines.length > index) {
      this.typeTagline(this.taglines[index], animate)
    }
  }

  typeTagline(text, animate = true) {
    // Remove existing cursor if any
    if (this.cursor) {
      this.cursor.remove()
      this.cursor = null
    }

    // Clear tagline
    this.taglineTarget.textContent = ""

    if (!animate) {
      this.taglineTarget.textContent = text
      return
    }

    // Create blinking dot cursor (matches chat streaming cursor)
    this.cursor = document.createElement("span")
    this.cursor.className = "streaming-cursor"
    this.taglineTarget.appendChild(this.cursor)

    // Start typing after 1 second delay
    this.typingTimeout = setTimeout(() => {
      this.typeCharacter(text, 0)
    }, 1000)
  }

  typeCharacter(text, index) {
    if (index < text.length) {
      // Insert character before cursor
      const textNode = document.createTextNode(text.charAt(index))
      this.taglineTarget.insertBefore(textNode, this.cursor)

      this.typingTimeout = setTimeout(() => {
        this.typeCharacter(text, index + 1)
      }, 30)
    } else {
      // Remove cursor after typing is done
      setTimeout(() => {
        if (this.cursor) {
          this.cursor.remove()
          this.cursor = null
        }
      }, 1500)
    }
  }

  animateTitleFadeUp(newText) {
    this.titleTarget.style.transition = "opacity 0.15s ease-out, transform 0.15s ease-out"
    this.titleTarget.style.opacity = "0"
    this.titleTarget.style.transform = "translateY(8px)"

    setTimeout(() => {
      this.titleTarget.textContent = newText
      this.titleTarget.style.transition = "opacity 0.25s ease-out, transform 0.25s ease-out"
      this.titleTarget.style.opacity = "1"
      this.titleTarget.style.transform = "translateY(0)"
    }, 150)
  }

  animateIconFadeUp(newClassName) {
    this.iconTarget.style.transition = "opacity 0.15s ease-out, transform 0.15s ease-out"
    this.iconTarget.style.opacity = "0"
    this.iconTarget.style.transform = "translateY(8px)"

    setTimeout(() => {
      this.iconTarget.className = newClassName
      this.iconTarget.style.transition = "opacity 0.25s ease-out, transform 0.25s ease-out"
      this.iconTarget.style.opacity = "1"
      this.iconTarget.style.transform = "translateY(0)"
    }, 150)
  }

  startAutoRotate() {
    this.autoRotateTimer = setInterval(() => {
      this.next()
    }, this.interval)
  }

  stopAutoRotate() {
    if (this.autoRotateTimer) {
      clearInterval(this.autoRotateTimer)
      this.autoRotateTimer = null
    }
  }

  restartAutoRotate() {
    if (this.autoRotate) {
      this.stopAutoRotate()
      this.startAutoRotate()
    }
  }

  // Pause on hover
  pause() {
    this.stopAutoRotate()
  }

  resume() {
    if (this.autoRotate) {
      this.startAutoRotate()
    }
  }

  // Open the model select dropdown
  openModelSelect() {
    const modelSelectButton = document.getElementById("selectModelButton")
    if (modelSelectButton) {
      modelSelectButton.click()
    }
  }
}
